import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { createTemplate, updateTemplate, getTemplate } from '../../services/missionTemplateService';
import clientService from '../../services/clientService';
import PageHeader from '../../components/common/PageHeader';
import GoogleAutocomplete from '../../components/map/GoogleAutocomplete';
import { useUser } from '../../context/UserContext';
import { useI18n } from '../../i18n/I18nContext';
import { ToastContainer, toast } from 'react-toastify';
import './MissionTemplate.css';

import api from '../../services/api';

const DAYS_OF_WEEK = [
  { code: 'MONDAY', name: 'Lundi' },
  { code: 'TUESDAY', name: 'Mardi' },
  { code: 'WEDNESDAY', name: 'Mercredi' },
  { code: 'THURSDAY', name: 'Jeudi' },
  { code: 'FRIDAY', name: 'Vendredi' },
  { code: 'SATURDAY', name: 'Samedi' },
  { code: 'SUNDAY', name: 'Dimanche' }
];

const MissionTemplateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { userType } = useUser();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(isEditMode);
  const [clients, setClients] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [remorques, setRemorques] = useState([]);
  const [excludedDateInput, setExcludedDateInput] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    departureLocation: '',
    arrivalLocation: '',
    departureLat: null,
    departureLng: null,
    arrivalLat: null,
    arrivalLng: null,
    cargoType: '',
    vehicleType: '',
    weight: '',
    length: '',
    volume: '',
    price: '',
    currency: 'EUR',
    description: '',
    expediteurNom: '',
    expediteurContact: '',
    expediteurTelephone: '',
    destinataireNom: '',
    destinataireContact: '',
    destinataireTelephone: '',
    isDangerousMaterial: false,
    requiredEquipment: [],
    daysOfWeek: [], // Stored as array locally, joined by comma for API
    timeSlots: [],
    isActive: true,
    excludedDates: [],
    clientId: '',
    isInternalMission: false,
    frequencyType: 'WEEKLY',
    intervalValue: 1,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    monthDay: 1,
    weekOfMonth: 1,
    mode: 'AUTOMATIC', // 'AUTOMATIC' or 'MANUAL' (for legacy logic)
    templateMode: 'SIMPLE', // 'SIMPLE', 'ADVANCED'
    defaultChauffeurId: '',
    defaultVehiculeId: '',
    defaultRemorqueId: '',
    defaultDepartureTime: '',
    defaultArrivalTime: '',
    anticipationMinutes: 720 // 12 hours by default
  });

  const vehicleOptionsByCargo = {
    BENNES: ['cerealiere', 'renforcee', 'fondMouvant', 'autre', 'tout'],
    CITERNES: ['pulveeLiquide', 'autre', 'tout'],
    CONTAINERS: ['20', '40', 'tout'],
    DEMENAGEMENTS: ['cat1', 'cat2', 'cat3', 'tout'],
    MARCHANDISES_GENERALES: ['taut', 'fourgon', 'plateau', 'autre', 'tout'],
    TEMPERATURE_DIRIGEE: ['frais', 'surgele', 'nonRefrigere', 'multiTemp', 'autre', 'tout'],
    EXPRESSE_ENVELOPPE: ['vehiculeLeger', 'moto', 'tout'],
    EXPRESSE_COLIS: ['vehiculeLeger', 'moto', 'tout'],
    MESSAGERIE_COURSES: ['neant'],
    TRANSPORTS_EXCEPTIONNEL: ['neant'],
    TRANSPORTS_VOLUMINEUX: ['neant'],
  };

  const currentVehicleOptions = vehicleOptionsByCargo[formData.cargoType] || [];

  useEffect(() => {
    const init = async () => {
      try {
        let clientsData = [];
        let chauffeursData = [];
        let vehiculesData = [];
        let remorquesData = [];
        
        try { clientsData = await clientService.getAllClients(); } catch (e) { console.error(e); }
        try { 
            const res = await api.get('/transporteur/drivers'); 
            chauffeursData = Array.isArray(res.data) ? res.data : (res.data.content || []);
        } catch (e) { console.error(e); }
        
        try { 
            const res = await api.get('/transporteur/vehicules'); 
            vehiculesData = Array.isArray(res.data) ? res.data : (res.data.content || []);
        } catch (e) { console.error(e); }

        try { 
            const res = await api.get('/transporteur/remorques'); 
            remorquesData = Array.isArray(res.data) ? res.data : (res.data.content || []);
        } catch (e) { console.error(e); }

        setClients(clientsData);
        setChauffeurs(chauffeursData);
        setVehicules(vehiculesData);
        setRemorques(remorquesData);


        if (isEditMode) {
          const template = await getTemplate(id);
          setFormData({
            ...template,
            daysOfWeek: template.daysOfWeek ? template.daysOfWeek.split(',') : [],
            excludedDates: template.excludedDates || [],
            clientId: template.clientId || '',
            isInternalMission: template.isInternalMission || false,
            frequencyType: template.frequencyType || 'WEEKLY',
            intervalValue: template.intervalValue || 1,
            startDate: template.startDate || new Date().toISOString().split('T')[0],
            endDate: template.endDate || '',
            monthDay: template.monthDay || 1,
            weekOfMonth: template.weekOfMonth || 1,
            timeSlots: template.timeSlots && template.timeSlots.length > 0 
                ? template.timeSlots.map(ts => ({
                    departureTime: ts.departureTime.substring(0, 5),
                    arrivalTime: ts.arrivalTime.substring(0, 5),
                    chauffeurId: ts.chauffeurId || '',
                    vehiculeId: ts.vehiculeId || '',
                    remorqueId: ts.remorqueId || '',
                    dayOfWeek: ts.dayOfWeek || ''
                  })) 
                : [],
            mode: template.frequencyType === 'MANUAL' ? 'MANUAL' : 'AUTOMATIC',
            templateMode: template.templateMode || 'SIMPLE',
            defaultChauffeurId: template.defaultChauffeurId || '',
            defaultVehiculeId: template.defaultVehiculeId || '',
            defaultRemorqueId: template.defaultRemorqueId || '',
            defaultDepartureTime: template.defaultDepartureTime ? template.defaultDepartureTime.substring(0, 5) : '',
            defaultArrivalTime: template.defaultArrivalTime ? template.defaultArrivalTime.substring(0, 5) : '',
            anticipationMinutes: template.anticipationMinutes || 720,
            expediteurNom: template.expediteurNom || '',
            expediteurContact: template.expediteurContact || '',
            expediteurTelephone: template.expediteurTelephone || ''
          });
        }
      } catch (err) {
        toast.error('Erreur lors de l\'initialisation');
      } finally {
        setInitLoading(false);
      }
    };
    init();
  }, [id, isEditMode]);

  const handleDepartureSelect = useCallback((place) => {
    if (place) {
      const coords = place.coordinates || place.center || [null, null];
      setFormData(prev => ({
        ...prev,
        departureLocation: place.name || place.place_name,
        departureLat: coords[1],
        departureLng: coords[0]
      }));
    }
  }, []);

  const handleArrivalSelect = useCallback((place) => {
    if (place) {
      const coords = place.coordinates || place.center || [null, null];
      setFormData(prev => ({
        ...prev,
        arrivalLocation: place.name || place.place_name,
        arrivalLat: coords[1],
        arrivalLng: coords[0]
      }));
    }
  }, []);

  const handleDayToggle = (dayCode) => {
    setFormData(prev => {
      const days = [...prev.daysOfWeek];
      if (days.includes(dayCode)) {
        return { ...prev, daysOfWeek: days.filter(d => d !== dayCode) };
      } else {
        return { ...prev, daysOfWeek: [...days, dayCode] };
      }
    });
  };

  const addExcludedDate = () => {
    if (excludedDateInput && !formData.excludedDates.includes(excludedDateInput)) {
      setFormData(prev => ({ ...prev, excludedDates: [...prev.excludedDates, excludedDateInput] }));
      setExcludedDateInput('');
    }
  };

  const removeExcludedDate = (dateToRemove) => {
    setFormData(prev => ({ ...prev, excludedDates: prev.excludedDates.filter(d => d !== dateToRemove) }));
  };

  const addTimeSlot = () => {
    setFormData(prev => ({
      ...prev,
      timeSlots: [...prev.timeSlots, { 
        departureTime: '08:00', 
        arrivalTime: '17:00', 
        chauffeurId: '', 
        vehiculeId: '', 
        remorqueId: '', 
        dayOfWeek: '' 
      }]
    }));
  };

  const removeTimeSlot = (index) => {
    if (formData.timeSlots.length <= 1) return;
    setFormData(prev => {
      const newSlots = [...prev.timeSlots];
      newSlots.splice(index, 1);
      return { ...prev, timeSlots: newSlots };
    });
  };

  const updateTimeSlot = (index, field, value) => {
    setFormData(prev => {
      const newSlots = [...prev.timeSlots];
      newSlots[index] = { ...newSlots[index], [field]: value };
      return { ...prev, timeSlots: newSlots };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mode === 'AUTOMATIC' && formData.frequencyType === 'WEEKLY' && formData.daysOfWeek.length === 0) {
      toast.error('Veuillez sélectionner au moins un jour de la semaine pour la fréquence hebdomadaire');
      return;
    }
    
    if (!formData.isInternalMission && !formData.clientId) {
      toast.error('Veuillez sélectionner un client ou cocher "Mission interne"');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        frequencyType: formData.mode === 'MANUAL' ? 'MANUAL' : formData.frequencyType,
        daysOfWeek: formData.daysOfWeek.join(','),
        // Send weekOfMonth only if appropriate
        weekOfMonth: formData.frequencyType === 'MONTHLY_DAY' ? formData.weekOfMonth : null,
        // Send correct time format HH:MM:SS
        timeSlots: formData.timeSlots.map(ts => ({
          departureTime: ts.departureTime.length === 5 ? `${ts.departureTime}:00` : ts.departureTime,
          arrivalTime: ts.arrivalTime.length === 5 ? `${ts.arrivalTime}:00` : ts.arrivalTime,
          chauffeurId: ts.chauffeurId || null,
          vehiculeId: ts.vehiculeId || null,
          remorqueId: ts.remorqueId || null,
          dayOfWeek: ts.dayOfWeek || null
        })),
        defaultChauffeurId: formData.defaultChauffeurId || null,
        defaultVehiculeId: formData.defaultVehiculeId || null,
        defaultRemorqueId: formData.defaultRemorqueId || null,
        defaultDepartureTime: formData.defaultDepartureTime ? `${formData.defaultDepartureTime}:00` : null,
        defaultArrivalTime: formData.defaultArrivalTime ? `${formData.defaultArrivalTime}:00` : null,
        anticipationMinutes: formData.anticipationMinutes ? parseInt(formData.anticipationMinutes) : 720,
        expediteurNom: formData.expediteurNom || null,
        expediteurContact: formData.expediteurContact || null,
        expediteurTelephone: formData.expediteurTelephone || null,
        destinataireNom: formData.destinataireNom || null,
        destinataireContact: formData.destinataireContact || null,
        destinataireTelephone: formData.destinataireTelephone || null
      };

      if (isEditMode) {
        await updateTemplate(id, payload);
        toast.success('Modèle mis à jour');
      } else {
        await createTemplate(payload);
        toast.success('Modèle créé avec succès');
      }
      setTimeout(() => navigate('/missions/recurring'), 1500);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (initLoading) {
    return (
      <DashboardLayout>
        <div className="text-center p-5 mt-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container py-4">
        <PageHeader
          title={isEditMode ? 'Modifier le Modèle' : 'Créer un Modèle Récurrent'}
          subtitle="Configurez les règles de génération automatique pour vos missions"
        />

        <form onSubmit={handleSubmit}>
          {/* Section 1: Informations Générales */}
          <div className="form-section-premium animate__animated animate__fadeInUp">
            <div className="section-header-premium p-4 border-bottom bg-light">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h2 className="mb-1 fw-bold text-dark">{isEditMode ? 'Modifier le modèle' : 'Nouveau modèle de mission'}</h2>
                  <p className="text-muted mb-0">Définissez vos missions régulières pour automatiser votre planning</p>
                </div>
                <div className="template-mode-toggle d-flex bg-white p-1 rounded-pill border shadow-sm" style={{ minWidth: '240px' }}>
                  <button 
                    type="button"
                    className={`btn flex-grow-1 rounded-pill fw-bold btn-sm ${formData.templateMode === 'SIMPLE' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setFormData({...formData, templateMode: 'SIMPLE'})}
                  >
                    <i className="bi bi-lightning-charge-fill me-1"></i> Simple
                  </button>
                  <button 
                    type="button"
                    className={`btn flex-grow-1 rounded-pill fw-bold btn-sm ${formData.templateMode === 'ADVANCED' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setFormData({...formData, templateMode: 'ADVANCED'})}
                  >
                    <i className="bi bi-gear-wide-connected me-1"></i> Avancé
                  </button>
                </div>
              </div>
              
              <div className="alert alert-info border-0 shadow-sm rounded-4 mb-0 py-2 px-3 d-flex align-items-center">
                <i className="bi bi-info-circle-fill me-2 fs-5"></i>
                <small className="fw-semibold">
                  {formData.templateMode === 'SIMPLE' 
                    ? "Mode Simple : Les missions seront créées pour toutes les occurrences futures." 
                    : "Mode Avancé : Les missions seront générées progressivement (anticipation)."}
                </small>
              </div>
            </div>

            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Nom du modèle"
                      required
                    />
                    <label>Nom du modèle</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating">
                    <select
                      className="form-select"
                      name="clientId"
                      value={formData.clientId}
                      onChange={handleChange}
                      disabled={formData.isInternalMission}
                      required={!formData.isInternalMission}
                    >
                      <option value="">{formData.isInternalMission ? 'Désactivé (Mission Interne)' : 'Sélectionner un client obligatoire'}</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.prenom} {c.nom} {c.entreprise ? `(${c.entreprise})` : ''}</option>
                      ))}
                    </select>
                    <label>Client</label>
                  </div>
                  <div className="form-check mt-2 ms-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="isInternalMission"
                      id="internalMissionCheck"
                      checked={formData.isInternalMission}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          isInternalMission: e.target.checked,
                          clientId: e.target.checked ? '' : formData.clientId
                        });
                      }}
                    />
                    <label className="form-check-label fw-bold text-primary small" htmlFor="internalMissionCheck">
                      Mission Interne (Sans client)
                    </label>
                  </div>
                </div>
                <div className="col-md-2 d-flex align-items-center justify-content-center">
                  <div className="form-check form-switch me-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      id="activationSwitch"
                    />
                    <label className="form-check-label fw-bold ms-2" htmlFor="activationSwitch">
                      {formData.isActive ? 'Actif' : 'Inactif'}
                    </label>
                  </div>
                </div>
              </div>
            
            <div className="row g-3 mt-1">
              <div className="col-md-12">
                <label className="form-label fw-bold small text-muted mb-2">MODE DE FONCTIONNEMENT</label>
                <div className="d-flex gap-3">
                  <div 
                    className={`mode-selector-card ${formData.mode === 'AUTOMATIC' ? 'active' : ''}`}
                    onClick={() => setFormData({...formData, mode: 'AUTOMATIC'})}
                  >
                    <div className="mode-icon"><i className="bi bi-robot"></i></div>
                    <div className="mode-info">
                      <div className="mode-name">Automatique</div>
                      <div className="mode-desc">Génère les missions selon un calendrier fixe</div>
                    </div>
                  </div>
                  <div 
                    className={`mode-selector-card ${formData.mode === 'MANUAL' ? 'active' : ''}`}
                    onClick={() => setFormData({...formData, mode: 'MANUAL'})}
                  >
                    <div className="mode-icon"><i className="bi bi-hand-index-thumb"></i></div>
                    <div className="mode-info">
                      <div className="mode-name">Manuel (Draft)</div>
                      <div className="mode-desc">Créez la mission uniquement quand vous cliquez sur le bouton</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Section 2: Itinéraire et Logistique */}
          <div className="form-section-premium animate__animated animate__fadeInUp" style={{ animationDelay: '0.1s' }}>
            <h3 className="section-title-premium">
              <i className="bi bi-truck-front-fill"></i> Itinéraire et Logistique
            </h3>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <GoogleAutocomplete
                  value={formData.departureLocation}
                  onChange={(e) => setFormData({ ...formData, departureLocation: e.target.value })}
                  onSelect={handleDepartureSelect}
                  placeholder="Lieu de départ"
                  required
                  inputClassName="form-control premium-input-form"
                  instanceId="departure_tmpl"
                />
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-12">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control premium-input-form"
                    name="expediteurNom"
                    value={formData.expediteurNom}
                    onChange={handleChange}
                    placeholder="Entreprise de chargement"
                  />
                  <label>Entreprise de chargement</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control premium-input-form"
                    name="expediteurContact"
                    value={formData.expediteurContact}
                    onChange={handleChange}
                    placeholder="Contact sur place (Chargement)"
                  />
                  <label>Contact sur place (Chargement)</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="tel"
                    className="form-control premium-input-form"
                    name="expediteurTelephone"
                    value={formData.expediteurTelephone}
                    onChange={handleChange}
                    placeholder="Téléphone (Chargement)"
                  />
                  <label>Téléphone (Chargement)</label>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <GoogleAutocomplete
                  value={formData.arrivalLocation}
                  onChange={(e) => setFormData({ ...formData, arrivalLocation: e.target.value })}
                  onSelect={handleArrivalSelect}
                  placeholder="Lieu d'arrivée"
                  required
                  inputClassName="form-control premium-input-form"
                  instanceId="arrival_tmpl"
                />
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-12">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control premium-input-form"
                    name="destinataireNom"
                    value={formData.destinataireNom}
                    onChange={handleChange}
                    placeholder="Nom du destinataire"
                  />
                  <label>Entreprise de livraison</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control premium-input-form"
                    name="destinataireContact"
                    value={formData.destinataireContact}
                    onChange={handleChange}
                    placeholder="Contact sur place (Livraison)"
                  />
                  <label>Contact sur place (Livraison)</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="tel"
                    className="form-control premium-input-form"
                    name="destinataireTelephone"
                    value={formData.destinataireTelephone}
                    onChange={handleChange}
                    placeholder="Téléphone (Livraison)"
                  />
                  <label>Téléphone (Livraison)</label>
                </div>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-md-4">
                <div className="form-floating">
                  <select className="form-select" name="cargoType" value={formData.cargoType} onChange={handleChange} required>
                    <option value="">{t('missionForm.placeholders.select')}</option>
                    <option value="BENNES">{t('missionForm.cargoTypes.bennes')}</option>
                    <option value="CITERNES">{t('missionForm.cargoTypes.citernes')}</option>
                    <option value="CONTAINERS">{t('missionForm.cargoTypes.containers')}</option>
                    <option value="DEMENAGEMENTS">{t('missionForm.cargoTypes.demenagements')}</option>
                    <option value="MARCHANDISES_GENERALES">{t('missionForm.cargoTypes.marchandisesGenerales')}</option>
                    <option value="TEMPERATURE_DIRIGEE">{t('missionForm.cargoTypes.temperatureDirigee')}</option>
                    <option value="EXPRESSE_ENVELOPPE">{t('missionForm.cargoTypes.expresseEnveloppe')}</option>
                    <option value="EXPRESSE_COLIS">{t('missionForm.cargoTypes.expresseColis')}</option>
                    <option value="MESSAGERIE_COURSES">{t('missionForm.cargoTypes.messagerieCourses')}</option>
                    <option value="TRANSPORTS_EXCEPTIONNEL">{t('missionForm.cargoTypes.transportsExceptionnel')}</option>
                    <option value="TRANSPORTS_VOLUMINEUX">{t('missionForm.cargoTypes.transportsVolumineux')}</option>
                  </select>
                  <label>{t('missionForm.fields.cargoType')}</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-floating">
                  {currentVehicleOptions.length > 0 && currentVehicleOptions[0] !== 'neant' ? (
                    <select
                      className="form-select"
                      name="vehicleType"
                      value={formData.vehicleType}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{t('missionForm.placeholders.select')}</option>
                      {currentVehicleOptions.map((opt) => (
                        <option key={opt} value={opt}>{t(`missionForm.vehicleTypes.${opt}`)}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      name="vehicleType"
                      value={formData.vehicleType}
                      onChange={handleChange}
                      required
                      placeholder={t('missionForm.placeholders.vehicleType')}
                    />
                  )}
                  <label>{t('missionForm.fields.vehicleType')}</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="input-group">
                  <div className="form-floating flex-grow-1">
                    <input
                      type="number"
                      className="form-control"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder={t('missionForm.placeholders.price')}
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                      required
                    />
                    <label htmlFor="price">{t('missionForm.fields.price')}</label>
                  </div>
                  <select
                    className="form-select bg-light"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    style={{ maxWidth: '5.625rem', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, fontWeight: '600' }}
                  >
                    <option value="EUR">EUR</option>
                    <option value="MAD">MAD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
            </div>

            <hr className="my-4 op-2" />

            <div className="row g-3">
              <div className="col-md-6">
                 <div className="alert alert-warning border shadow-sm p-3 mb-0">
                    <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-clock-fill me-2 fs-5"></i>
                        <h6 className="mb-0 fw-bold">Horaire Principal de Mission</h6>
                    </div>
                    <p className="small text-muted mb-3">Cet horaire sera généré pour TOUS les jours sélectionnés dans vos paramètres de récurrence.</p>
                    <div className="row g-2">
                        <div className="col-md-6">
                            <div className="form-floating">
                                <input
                                    type="time"
                                    className="form-control"
                                    name="defaultDepartureTime"
                                    value={formData.defaultDepartureTime}
                                    onChange={handleChange}
                                    required
                                />
                                <label>Départ par défaut</label>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-floating">
                                <input
                                    type="time"
                                    className="form-control"
                                    name="defaultArrivalTime"
                                    value={formData.defaultArrivalTime}
                                    onChange={handleChange}
                                    required
                                />
                                <label>Arrivée par défaut</label>
                            </div>
                        </div>
                    </div>
                 </div>
              </div>
            </div>
          </div>

          {/* Section 3: Récurrence (Only for AUTOMATIC mode) */}
          {formData.mode === 'AUTOMATIC' && (
            <div className="form-section-premium animate__animated animate__fadeInUp" style={{ animationDelay: '0.2s' }}>
              <h3 className="section-title-premium">
                <i className="bi bi-calendar-check-fill"></i> Paramètres de Récurrence
              </h3>

              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <label className="form-label fw-bold">Type de fréquence</label>
                  <select 
                    className="form-select premium-input-form" 
                    name="frequencyType" 
                    value={formData.frequencyType} 
                    onChange={handleChange}
                  >
                    <option value="DAILY">Quotidien</option>
                    <option value="WEEKLY">Hebdomadaire</option>
                    <option value="MONTHLY_DATE">Mensuel (Jour précis du mois)</option>
                    <option value="MONTHLY_DAY">Mensuel (Jour de la semaine, ex: 1er Lundi)</option>
                    <option value="CUSTOM">Personnalisé (X jours)</option>
                  </select>
                </div>
                
                <div className="col-md-2">
                  <label className="form-label fw-bold">Intervalle</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">Tous les</span>
                    <input 
                      type="number" 
                      className="form-control border-start-0 ps-0" 
                      name="intervalValue" 
                      value={formData.intervalValue} 
                      onChange={handleChange} 
                      min="1" 
                    />
                  </div>
                  <small className="text-muted mt-1">
                    {formData.frequencyType === 'WEEKLY' && 'semaine(s)'}
                    {(formData.frequencyType === 'DAILY' || formData.frequencyType === 'CUSTOM') && 'jour(s)'}
                    {(formData.frequencyType === 'MONTHLY_DATE' || formData.frequencyType === 'MONTHLY_DAY') && 'mois'}
                  </small>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Date de début</label>
                  <input 
                    type="date" 
                    className="form-control premium-input-form" 
                    name="startDate" 
                    value={formData.startDate} 
                    onChange={handleChange} 
                    required 
                  />
                </div>
                
                <div className="col-md-3">
                  <label className="form-label fw-bold">Date de fin</label>
                  <input 
                    type="date" 
                    className="form-control premium-input-form" 
                    name="endDate" 
                    value={formData.endDate} 
                    onChange={handleChange}
                    required
                  />
                  {formData.startDate && formData.endDate && (() => {
                    const diffDays = Math.round((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24));
                    return diffDays > 180 ? (
                      <div className="alert alert-warning mt-2 p-2 small mb-0">
                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                        Plage de <strong>{diffDays} jours</strong> détectée. La génération sera limitée à <strong>365 missions maximum</strong>.
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {formData.frequencyType === 'WEEKLY' && (
                <div className="mb-4 bg-light p-3 rounded border">
                  <label className="form-label fw-bold mb-3 text-primary d-block">Jours de la semaine</label>
                  <div className="days-selector">
                    {DAYS_OF_WEEK.map(day => (
                      <div key={day.code} className="day-checkbox-btn">
                        <input
                          type="checkbox"
                          id={`day-${day.code}`}
                          checked={formData.daysOfWeek.includes(day.code)}
                          onChange={() => handleDayToggle(day.code)}
                        />
                        <label htmlFor={`day-${day.code}`} className="day-label">
                          <span className="day-name">{day.name}</span>
                          <span className="day-code">{day.code.substring(0, 3)}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.frequencyType === 'MONTHLY_DATE' && (
                <div className="mb-4 bg-light p-3 rounded border">
                  <div className="row">
                    <div className="col-md-4">
                      <label className="form-label fw-bold text-primary">Jour du mois</label>
                      <div className="input-group">
                        <span className="input-group-text">Le</span>
                        <input 
                          type="number" 
                          className="form-control" 
                          name="monthDay" 
                          value={formData.monthDay} 
                          onChange={handleChange} 
                          min="1" 
                          max="31" 
                        />
                        <span className="input-group-text">du mois</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formData.frequencyType === 'MONTHLY_DAY' && (
                <div className="mb-4 bg-light p-3 rounded border">
                  <label className="form-label fw-bold mb-3 text-primary d-block">Configurer le jour du mois (Ex: Le 1er Lundi)</label>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="form-floating">
                        <select className="form-select" name="weekOfMonth" value={formData.weekOfMonth} onChange={handleChange}>
                          <option value="1">Premier (1er)</option>
                          <option value="2">Deuxième (2ème)</option>
                          <option value="3">Troisième (3ème)</option>
                          <option value="4">Quatrième (4ème)</option>
                          <option value="-1">Dernier</option>
                        </select>
                        <label>Position dans le mois</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-floating">
                        <select 
                          className="form-select" 
                          value={formData.daysOfWeek[0] || ''} 
                          onChange={(e) => setFormData({...formData, daysOfWeek: [e.target.value]})}
                          required
                        >
                          <option value="">Sélectionner un jour</option>
                          {DAYS_OF_WEEK.map(day => (
                            <option key={day.code} value={day.code}>{day.name}</option>
                          ))}
                        </select>
                        <label>Jour de la semaine</label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <hr className="my-4 text-muted border-2" />

              {/* Ressources par défaut + Anticipation */}
              <div className="mb-4 p-3 bg-light rounded border border-info">
                <h5 className="fw-bold text-info mb-3"><i className="bi bi-people-fill me-2"></i>Ressources & Anticipation</h5>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-bold small">Chauffeur par défaut <span className="text-muted fw-normal">(Optionnel)</span></label>
                    <select
                      className="form-select"
                      name="defaultChauffeurId"
                      value={formData.defaultChauffeurId}
                      onChange={handleChange}
                    >
                      <option value="">-- Aucun chauffeur par défaut --</option>
                      {chauffeurs.map(c => (
                        <option key={c.id} value={c.id}>{c.firstname} {c.lastname}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-bold small">Véhicule par défaut <span className="text-muted fw-normal">(Optionnel)</span></label>
                    <select
                      className="form-select"
                      name="defaultVehiculeId"
                      value={formData.defaultVehiculeId}
                      onChange={handleChange}
                    >
                      <option value="">-- Aucun véhicule par défaut --</option>
                      {vehicules.map(v => (
                        <option key={v.id} value={v.id}>{v.brand} {v.model} - {v.plateNumber}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-bold small">Remorque par défaut <span className="text-muted fw-normal">(Optionnel)</span></label>
                    <select
                      className="form-select"
                      name="defaultRemorqueId"
                      value={formData.defaultRemorqueId}
                      onChange={handleChange}
                    >
                      <option value="">-- Aucune remorque par défaut --</option>
                      {remorques.map(r => (
                        <option key={r.id} value={r.id}>{r.brand} {r.model} - {r.plateNumber}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {formData.templateMode === 'ADVANCED' && (
                  <div className="row g-3 mt-2 pt-2 border-top">
                    <div className="col-md-12">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                            <label className="form-label fw-bold small mb-0">Délai d'anticipation <span className="text-muted fw-normal">(minutes)</span></label>
                        </div>
                        <div className="input-group" style={{ maxWidth: '200px' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            name="anticipationMinutes"
                            value={formData.anticipationMinutes}
                            onChange={handleChange}
                            min="0"
                            step="60"
                          />
                          <span className="input-group-text form-control-sm">min</span>
                        </div>
                        <div className="ms-3">
                           <small className="text-muted">
                              Génération {Math.round(formData.anticipationMinutes / 60)}h avant le départ.
                           </small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* TODO: ROTATIONS - Hidden temporarily. To restore, remove the {false && (...)} wrapper below. */}
              {false && <div className="time-slots-container mb-4" style={{display:'none'}}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-1 text-primary fw-bold">Rotations supplémentaires (Optionnel)</h5>
                    <p className="small text-muted mb-0">Ajoutez des départs supplémentaires ou des départs spécifiques à certains jours.</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm rounded-pill"
                    onClick={addTimeSlot}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Ajouter une rotation
                  </button>
                </div>
                
                {formData.timeSlots.map((slot, index) => (
                  <div key={index} className="mb-3 p-3 bg-white border rounded shadow-sm">
                    {/* Row 1: Times + Delete */}
                    <div className="row g-3 align-items-center mb-2">
                      <div className="col-md-2 d-flex justify-content-center align-items-center">
                        <span className="badge bg-primary fs-6 py-2 px-3">Rotation {index + 1}</span>
                      </div>
                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="time"
                            className="form-control fw-bold text-primary"
                            value={slot.departureTime}
                            onChange={(e) => updateTimeSlot(index, 'departureTime', e.target.value)}
                            required
                          />
                          <label>Heure de départ</label>
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="form-floating">
                          <input
                            type="time"
                            className="form-control fw-bold text-primary"
                            value={slot.arrivalTime}
                            onChange={(e) => updateTimeSlot(index, 'arrivalTime', e.target.value)}
                            required
                          />
                          <label>Heure d'arrivée estimée</label>
                        </div>
                      </div>
                      <div className="col-md-1 d-flex justify-content-center align-items-center">
                        {formData.timeSlots.length > 1 && (
                          <i
                            className="bi bi-dash-circle-fill text-danger"
                            style={{ cursor: 'pointer', fontSize: '1.1rem' }}
                            onClick={() => removeTimeSlot(index)}
                            title="Supprimer cette rotation"
                          ></i>
                        )}
                      </div>
                    </div>
                    {/* Row 2: Per-rotation resource override */}
                    <div className="row g-2 mt-1 border-top pt-2 align-items-center">
                      <div className="col-md-2">
                        <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                          <i className="bi bi-arrow-repeat me-1"></i>Spécifique à cette rotation
                        </small>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select form-select-sm"
                          value={slot.chauffeurId || ''}
                          onChange={(e) => updateTimeSlot(index, 'chauffeurId', e.target.value)}
                        >
                          <option value="">-- Chauffeur Template --</option>
                          {chauffeurs.map(c => (
                            <option key={c.id} value={c.id}>{c.firstname} {c.lastname}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select form-select-sm"
                          value={slot.vehiculeId || ''}
                          onChange={(e) => updateTimeSlot(index, 'vehiculeId', e.target.value)}
                        >
                          <option value="">-- Véhicule Template --</option>
                          {vehicules.map(v => (
                            <option key={v.id} value={v.id}>{v.brand} {v.model} - {v.plateNumber}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select form-select-sm"
                          value={slot.remorqueId || ''}
                          onChange={(e) => updateTimeSlot(index, 'remorqueId', e.target.value)}
                        >
                          <option value="">-- Remorque Template --</option>
                          {remorques.map(r => (
                            <option key={r.id} value={r.id}>{r.brand} {r.model} - {r.plateNumber}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Row 3: Day selective override */}
                    <div className="row g-2 mt-1 border-top pt-2 align-items-center">
                      <div className="col-md-2">
                        <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                          <i className="bi bi-calendar-event me-1"></i>Jour de validité
                        </small>
                      </div>
                      <div className="col-md-10">
                        <select
                          className="form-select form-select-sm"
                          value={slot.dayOfWeek || ''}
                          onChange={(e) => updateTimeSlot(index, 'dayOfWeek', e.target.value)}
                        >
                          <option value="">Tous les jours sélectionnés du modèle</option>
                          {DAYS_OF_WEEK.map(day => (
                            <option key={day.code} value={day.code}>{day.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>}
              {/* END ROTATIONS SECTION - To restore, remove the {false && ...} wrapper in MissionTemplateForm.js around the time-slots-container div */}
              {/* Preview Block */}
              <div className="mt-4 p-3 bg-white border border-primary rounded shadow-sm">
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-info-circle-fill text-primary fs-4 me-2"></i>
                  <h5 className="m-0 fw-bold text-dark">Aperçu de la génération</h5>
                </div>
                <p className="text-muted mb-0 ms-4">
                  {formData.templateMode === 'SIMPLE' 
                    ? `En mode Simple, les missions seront créées immédiatement dès l'enregistrement.` 
                    : `En mode Avancé, les missions seront créées automatiquement ${Math.round(formData.anticipationMinutes / 60)}h avant chaque départ.`}
                  <br/>
                  <strong>Période :</strong> Du {formData.startDate || '...'} au {formData.endDate || 'fin indéterminée'}
                </p>
              </div>
            </div>
          )}

          {/* Section 4: Exceptions (Only for AUTOMATIC mode) */}
          {formData.mode === 'AUTOMATIC' && (
            <div className="form-section-premium animate__animated animate__fadeInUp" style={{ animationDelay: '0.3s' }}>
              <h3 className="section-title-premium">
                <i className="bi bi-calendar-x-fill"></i> Dates Exclues (Jours fériés, congés...)
              </h3>
              <div className="row g-3 align-items-end mb-3">
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="date"
                      className="form-control"
                      value={excludedDateInput}
                      onChange={(e) => setExcludedDateInput(e.target.value)}
                    />
                    <label>Sélectionner une date à exclure</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <button type="button" className="btn btn-outline-primary h-100 py-3 px-4" onClick={addExcludedDate}>
                    <i className="bi bi-plus-lg me-2"></i> Ajouter l'exception
                  </button>
                </div>
              </div>
              <div className="excluded-dates-list d-flex flex-wrap gap-2">
                {formData.excludedDates.map(date => (
                  <div key={date} className="badge bg-light text-dark border p-2 d-flex align-items-center gap-2">
                    <i className="bi bi-calendar-x"></i>
                    {new Date(date).toLocaleDateString()}
                    <button type="button" className="btn-close small" onClick={() => removeExcludedDate(date)} style={{ fontSize: '0.6rem' }}></button>
                  </div>
                ))}
                {formData.excludedDates.length === 0 && <span className="text-muted small">Aucune date d'exclusion configurée</span>}
              </div>
            </div>
          )}

          <div className="d-flex gap-3 mt-4 mb-5 justify-content-end">
            <button type="button" className="btn btn-outline-secondary px-5 py-3 rounded-pill" onClick={() => navigate('/missions/recurring')}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary px-5 py-3 rounded-pill shadow" disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-check-lg me-2"></i>}
              {isEditMode ? 'Enregistrer les modifications' : 'Créer le modèle'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default MissionTemplateForm;
